<template>
  <div>
    <!-- Invoice Info -->
    <invoice-details
      v-if="invoiceInfo"
      :invoice-info="invoiceInfo"
    />
    <!-- Payment Methods -->
    <payment-methods
      v-if="!disallowPaymentMethods"
      :key="paymentMethodsKey"
      :mode="mode"
      :invoice="paymentInvoice"
      :client-secret="clientSecret"
      :default-activate="activatePaymentMethod"
      :disable-activate="disableActivatePaymentMethod"
      :hide-activate="hideActivatePaymentMethod"
      :disallow-new="disallowNewPaymentMethod"
      :allowed-payment-method-types="allowedPaymentMethodTypes"
      @changed="paymentMethodChanged"
      @activate-changed="paymentMethodActivateChanged"
    />
  </div>
</template>

<script setup lang="ts">
import {
  PropType, Ref, ref, onMounted, watch, computed, inject,
} from 'vue';
import i18n from '@/plugins/i18n';

import { StripeElements } from '@stripe/stripe-js';
import {
  Customer, Invoice, InvoiceStatus, Price, ProductType, SetupIntent, SubscriptionStatus,
} from '@/api/api-billing';
import { actionShowError, performAction } from '@/services/vue-utils';
import store from '@/store/store';
import { invoiceInfo as getInvoiceInfo } from '@/services/billing-utils';
import { v4 as uuidv4 } from 'uuid';
import {
  PaymentContextMode, PaymentContext, preparePayment, submitPayment, cancelPayment,
} from '@/services/payment-details-utils';
import { PaymentWizardMode, PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';
import InvoiceDetails from './InvoiceDetails.vue';
import PaymentMethods, { PaymentMethodChangedEvent } from './PaymentMethods.vue';

export interface PaymentFinishedEvent {
  finishedInvoice: Invoice,
  finishedSetupIntent?: SetupIntent,
}

const props = defineProps({
  mode: { type: String as PropType<PaymentWizardMode>, default: undefined },
  price: { type: Object as PropType<Price|undefined>, default: undefined },
  previewInvoice: { type: Object as PropType<Invoice|undefined>, default: undefined },
  invoice: { type: Object as PropType<Invoice|undefined>, required: false, default: undefined },
  prices: { type: Array as PropType<Array<Price>|undefined>, default: undefined },
});

const emit = defineEmits<{(e: 'payment-ready', event: string|StripeElements|undefined): void
  (e: 'payment-finished', event: PaymentFinishedEvent): void
}>();

let paymentContext = undefined as PaymentContext|undefined;

const clientSecret = ref(undefined) as Ref<string|undefined>;

const paymentMethod = ref(undefined) as Ref<string|StripeElements|undefined>;
const paymentMethodType = ref(undefined) as Ref<string|undefined>;

const customer = computed(() => (store.getters.billingCustomer ?? undefined) as Customer|undefined);

const paymentInvoice = ref(undefined) as Ref<Invoice|undefined>;

const paymentMethodsKey = ref(0) as Ref<number>;
const activatePaymentMethod = ref(true) as Ref<boolean>;
const disableActivatePaymentMethod = ref(false) as Ref<boolean>;
const hideActivatePaymentMethod = ref(false) as Ref<boolean>;
const disallowNewPaymentMethod = ref(false) as Ref<boolean>;
const disallowPaymentMethods = ref(false) as Ref<boolean>;

const allowedPaymentMethodTypes = ref(undefined) as Ref<string[]|undefined>;

const invoiceInfo = computed(() => {
  const invoice = paymentInvoice.value;
  if (!invoice) return undefined;

  // negative value = customer has credit
  const balance = customer.value?.balance ?? 0;
  const cashBalance = -(customer.value?.cashBalances?.find((cb) => cb.currency === invoice.currency)?.amount ?? 0);
  const info = getInvoiceInfo(invoice, { balance, cashBalance });

  return info;
});

const paymentMethodChanged = (value: PaymentMethodChangedEvent|undefined) => {
  paymentMethod.value = value?.paymentMethod;
  paymentMethodType.value = value?.paymentMethodType;
};
const paymentMethodActivateChanged = (value: boolean) => { activatePaymentMethod.value = value; };

watch(
  () => paymentMethod.value,
  (value) => { emit('payment-ready', paymentMethod.value); },
  { immediate: true },
);

function createContext() {
  // subscription
  const subscription = customer.value?.subscription && customer.value?.subscription.status !== SubscriptionStatus.Ended
    ? customer.value?.subscription
    : undefined;

  // context
  const context = {
    customer: customer.value,
    mode: 'none',
    idempotencyKey: uuidv4(),
  } as PaymentContext;

  if (props.price?.product?.type === ProductType.Subscription) {
    // subscription mode
    context.priceID = props.price.ID;
    context.previewInvoice = props.previewInvoice;

    if (subscription) {
      // subscription modify mode
      context.mode = PaymentContextMode.SubscriptionModify;
    } else {
      // subscription create mode
      context.mode = PaymentContextMode.SubscriptionCreate;
    }
  } else if (props.invoice) {
    // invoice mode
    context.mode = PaymentContextMode.Invoice;
    context.invoice = props.invoice;
  } else if (props.prices) {
    // invoice for prices mode
    context.mode = PaymentContextMode.Prices;
    context.priceIDs = props.prices.flatMap((p) => p.ID!);
  }

  return context;
}

async function prepare() {
  if (!paymentContext) paymentContext = createContext();

  if (paymentContext.mode === PaymentContextMode.None) {
    actionShowError(new Error('Prepare payment: Unsupported payment mode'));
    paymentContext = undefined;
    return false;
  }

  const ok = await performAction(null, i18n.tc('error.general'), async () => preparePayment(paymentContext!));
  if (!ok) return false;

  clientSecret.value = paymentContext.setupIntent?.clientSecret;
  paymentInvoice.value = paymentContext.invoice ?? paymentContext.previewInvoice;

  activatePaymentMethod.value = paymentContext.activatePaymentMethod ?? false;
  disableActivatePaymentMethod.value = paymentContext.disableActivatePaymentMethod ?? true;
  hideActivatePaymentMethod.value = paymentContext.hideActivatePaymentMethod ?? true;
  disallowNewPaymentMethod.value = paymentContext.disallowNewPaymentMethod ?? false;
  disallowPaymentMethods.value = paymentContext.disallowPaymentMethods ?? false;
  allowedPaymentMethodTypes.value = paymentContext.allowedPaymentMethodTypes;

  paymentMethod.value = undefined;
  paymentMethodsKey.value += 1;

  return true;
}

async function submit() {
  if (!paymentContext
  || (!disallowPaymentMethods.value && (!paymentMethod.value || !paymentMethodType.value))) return undefined;

  paymentContext.paymentMethod = paymentMethod.value;
  paymentContext.paymentMethodType = paymentMethodType.value;
  paymentContext.activatePaymentMethod = activatePaymentMethod.value;

  const ok = await performAction(null, i18n.tc('error.general'), () => submitPayment(paymentContext!));
  if (!ok) return undefined;

  if (paymentContext.warningText) {
    actionShowError(new Error(paymentContext.warningText));
  }

  // return event
  const event = {
    finishedInvoice: paymentContext.invoice,
    finishedSetupIntent: paymentContext.setupIntent,
  } as PaymentFinishedEvent;
  emit('payment-finished', event);
  return event;
}

async function cancel() {
  await performAction(null, i18n.tc('error.general'), () => cancelPayment(paymentContext!));
}

defineExpose({ submit });

// wizard embeddable
const addStep = inject('addStep') as ((s: PaymentWizardStep) => void) | undefined;
const stepChanged = inject('stepChanged') as ((s: PaymentWizardStep) => void) | undefined;

const payText = computed(() => {
  if (!invoiceInfo.value) return undefined;

  const text = invoiceInfo.value.futureProrationDate ? i18n.tc('button.confirm') : i18n.tc('button.pay');
  return `${text} ${invoiceInfo.value.amountDue}`;
});

const paymentWizardStep = computed(() => (
  {
    key: PaymentWizardStepKey.PaymentDetails,
    label: i18n.tc('title.payment_details'),
    complete: paymentMethod.value !== undefined || disallowPaymentMethods,

    submitText: payText.value,
    submitColor: 'success',
    prepareAction: prepare,
    submitAction: submit,
    cancelAction: cancel,
  } as PaymentWizardStep));

watch(
  () => paymentWizardStep.value,
  (value) => { if (stepChanged) stepChanged(paymentWizardStep.value); },
);

if (addStep) addStep(paymentWizardStep.value);

</script>
